import { Fragment, useEffect, useState } from "react";
import { CANONICAL_SYMBOLS, INDEX_SYMBOLS, distinctSymbolWarnings, symbolSpecWarnings, type SymbolSpecField, type SymbolSpecWarning } from "@match/shared";
import { Button, ErrorNote, Field, SidePanel, inputClass, useCurrentUser } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { api, errorMessage, type SymbolInput } from "@/lib/api";
import { useRefreshDirectory, useServers } from "@/lib/queries";
import { decimalInput, intInput } from "@/lib/units";
import type { BrokerServer, SymbolCandidate, SymbolCandidates, SymbolMapping } from "@/lib/types";

/*
 * I SIMBOLI DI UN SERVER: la tabella, la mappatura (rileva / a mano) e la
 * proposta dal server fratello.
 *
 * Stavano nella pagina Server; da quando i server si guardano dalla prop firm o
 * dal broker che li possiede, li usano due pagine, quindi vivono qui.
 */

/** Decimal dal DB senza zeri di coda: "0.01000000" -> "0.01". */
const trim = (d: string) => String(Number(d));

/** `+180` -> `UTC+03:00`; `-330` -> `UTC-05:30`. */
function utcOffset(min: number): string {
  const segno = min < 0 ? "-" : "+";
  const a = Math.abs(min);
  return `UTC${segno}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

/**
 * IL FUSO DEL SERVER: quello letto, e quando.
 *
 * Non c'e' piu' un secondo valore accanto — il campo scritto a mano e' sparito
 * dal form, perche' il 19/09 era **lui** a essere sbagliato di un'ora
 * (`Etc/GMT-2` contro il 3 che rispondeva il server). Quindi qui non c'e' una
 * divergenza da segnalare: c'e' una misura, con la sua data.
 *
 * Dove non e' stato letto si scrive **«fuso non ancora letto»**, e non un
 * ripiego: quel numero decide la mezzanotte MT5, e con lei il reset del DD
 * giornaliero.
 */
export function FusoLetto({ server }: { server: BrokerServer }) {
  if (server.tzOffsetMin === null) {
    return (
      <span className="text-sm text-warn" title="Nessun conto di questo server si è ancora connesso. Finché il fuso non è letto non si calcola nessuna mezzanotte e nessun orario del gateway: si legge alla prima connessione.">
        fuso non ancora letto
      </span>
    );
  }
  const quando = server.tzReadAt ? new Date(server.tzReadAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : null;
  return (
    <span className="text-sm text-faint" title="Letto dal gateway (/ServerTimezone) all'ultima connessione di un conto di questo server. Decide la mezzanotte MT5 e la conversione di ogni orario che arriva dal gateway.">
      {utcOffset(server.tzOffsetMin)}
      {quando ? `, letto il ${quando}` : ""}
    </span>
  );
}

// ---------------------------------------------------------------------------

type ContoCollegato = { id: string; label: string; mt5Login: string };

/**
 * IL CONTO DA CUI SI LEGGONO I SIMBOLI (09/10).
 *
 * Sullo stesso server convivono gruppi di conti che vedono nomi diversi dello
 * stesso strumento (sul server FPG `XAUUSD-E` e `XAUUSD-P`): la lettura mostra
 * i nomi che vede il conto scelto, e scrive nel catalogo la riga del nome
 * scelto — le altre varianti non cambiano. Con un conto collegato solo la
 * scelta e' quella; con piu' d'uno la sceglie la persona, **senza
 * preselezione**.
 */
function useReadingAccount(serverId: string) {
  const [conti, setConti] = useState<ContoCollegato[] | null>(null);
  const [scelto, setScelto] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    api.servers
      .connectedAccounts(serverId)
      .then((c) => {
        if (!vivo) return;
        setConti(c);
        if (c.length === 1) setScelto(c[0]!.id);
      })
      .catch((e) => vivo && setErrore(errorMessage(e)));
    return () => {
      vivo = false;
    };
  }, [serverId]);
  return { conti, scelto, setScelto, errore };
}

function ReadingAccountPicker({ r }: { r: ReturnType<typeof useReadingAccount> }) {
  if (r.errore) return <ErrorNote>{r.errore}</ErrorNote>;
  if (!r.conti) return <p className="text-xs text-faint">Lettura dei conti collegati…</p>;
  const nome = (c: ContoCollegato) => `${c.label} · ${c.mt5Login}`;
  if (r.conti.length === 0) return null;
  if (r.conti.length === 1) {
    return (
      <p className="text-xs text-muted">
        Letto da <span className="machine-id">{nome(r.conti[0]!)}</span>, l&apos;unico conto collegato su questo server.
      </p>
    );
  }
  return (
    <Field
      label="Leggi dal conto"
      hint="Gruppi di conti dello stesso server possono vedere nomi diversi dello stesso simbolo (XAUUSD-E, XAUUSD-P). Si vedono i nomi del conto scelto, e si scrive nel catalogo la riga del nome scelto: le altre varianti non cambiano."
    >
      {(id) => (
        <select id={id} value={r.scelto ?? ""} onChange={(e) => r.setScelto(e.target.value || null)} className={inputClass}>
          <option value="">Scegli il conto</option>
          {r.conti!.map((c) => (
            <option key={c.id} value={c.id}>
              {nome(c)}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/** Serve scegliere un conto prima di leggere: piu' d'uno collegato e nessuno scelto. */
const needsChoice = (r: ReturnType<typeof useReadingAccount>) => !!r.conti && r.conti.length > 1 && !r.scelto;

// ---------------------------------------------------------------------------

/**
 * La mappatura di un simbolo (DECISIONE 27).
 *
 * La strada normale e' **rilevare**: il server sa quali simboli ha, e i numeri
 * che contano li sa lui, non noi. L'operatore sceglie il nome fra quelli veri e
 * i parametri li scrive il backend rileggendoli dal gateway.
 *
 * La scrittura a mano resta come ripiego, perche' senza un conto collegato su
 * quel server non c'e' niente da rilevare — ma i valori scritti a mano restano
 * NON verificati, e si vede.
 */
export function SymbolPanel({
  server,
  symbol,
  canonical: presetCanonical,
  peersOf,
  autoDetect,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  peersOf: (serverId: string, canonical: string) => string[];
  /**
   * Legge i candidati appena il pannello si apre, col canonico gia' scritto:
   * e' «Mappa adesso» della pagina Operazione, dove si arriva sapendo QUALE
   * simbolo manca. La scelta resta della persona, senza preselezione.
   */
  autoDetect?: boolean;
  onClose: () => void;
}) {
  const [manuale, setManuale] = useState(false);
  if (manuale) {
    return <SymbolManualForm server={server} symbol={symbol} canonical={presetCanonical} peersOf={peersOf} onClose={onClose} />;
  }
  return <SymbolDetect server={server} symbol={symbol} canonical={presetCanonical} autoDetect={autoDetect} onManuale={() => setManuale(true)} onClose={onClose} />;
}

function SymbolDetect({
  server,
  symbol,
  canonical: presetCanonical,
  autoDetect,
  onManuale,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  autoDetect?: boolean;
  onManuale: () => void;
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [canonical, setCanonical] = useState(symbol?.canonical ?? presetCanonical ?? "");
  const [found, setFound] = useState<SymbolCandidates | null>(null);
  /** Nessuna preselezione, nemmeno con un candidato solo: e' il punto. */
  const [scelto, setScelto] = useState<string | null>(null);
  const [busy, setBusy] = useState<"rileva" | "salva" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [senzaConto, setSenzaConto] = useState(false);
  const lettore = useReadingAccount(server.id);

  const rileva = async () => {
    const c = canonical.trim().toUpperCase();
    if (!c) return setError("Scrivi il simbolo canonico, per esempio XAUUSD.");
    if (needsChoice(lettore)) return setError("Scegli da quale conto leggere: su questo server ne sono collegati più d'uno.");
    setError(null);
    setSenzaConto(false);
    setFound(null);
    setScelto(null);
    setBusy("rileva");
    try {
      setFound(await api.servers.candidates(server.id, c, lettore.scelto));
    } catch (err) {
      const msg = errorMessage(err);
      setSenzaConto(/conto collegato/i.test(msg));
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  // Una volta sola, appena si sa da quale conto leggere: poi «Rileva» resta il pulsante di sempre.
  const [autoFatto, setAutoFatto] = useState(false);
  useEffect(() => {
    if (!autoDetect || autoFatto || !lettore.conti || needsChoice(lettore)) return;
    setAutoFatto(true);
    if (canonical.trim()) void rileva();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lettore.conti, lettore.scelto]);

  const salva = async () => {
    if (!scelto) return;
    setError(null);
    setBusy("salva");
    try {
      await api.servers.syncSymbol(server.id, { canonical: canonical.trim().toUpperCase(), brokerSymbol: scelto, accountId: found?.readFrom.accountId ?? lettore.scelto });
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const num = (x: number) => String(Number(x));

  return (
    <SidePanel
      open
      onClose={onClose}
      title={symbol ? `Rileva ${symbol.canonical}` : "Mappa un simbolo"}
      subtitle={`${server.displayName ?? server.name} · i nomi e i numeri li legge il broker, la corrispondenza la scegli tu`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" onClick={salva} disabled={!scelto || busy !== null}>
            {busy === "salva" ? "Salvataggio…" : scelto ? `Usa ${scelto}` : "Scegli un simbolo"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ReadingAccountPicker r={lettore} />
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Simbolo canonico" hint="Il nome nostro, uguale su tutti i server. Per esempio XAUUSD.">
              {(id) => (
                <input
                  id={id}
                  value={canonical}
                  onChange={(e) => setCanonical(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), rileva())}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          <Button onClick={rileva} disabled={busy !== null}>
            {busy === "rileva" ? "Lettura…" : "Rileva dal broker"}
          </Button>
        </div>

        {senzaConto ? (
          <div role="status" className="space-y-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            <p>{error}</p>
            <p className="text-xs text-warn/80">
              I simboli si leggono da una sessione aperta: senza un conto collegato su questo server non c'è niente da
              rilevare.
            </p>
            <Button onClick={onManuale}>Scrivi a mano (non verificato)</Button>
          </div>
        ) : (
          <ErrorNote>{error}</ErrorNote>
        )}

        {found && (
          <>
            <p className="text-xs text-faint">
              {found.candidates.length === 0
                ? `Nessuno dei ${found.symbolsOnServer} simboli di questo server somiglia a ${canonical.trim().toUpperCase()}.`
                : `${found.candidates.length} su ${found.symbolsOnServer} simboli visti dal conto ${found.readFrom.label}. Nessuno è preselezionato: guarda il contract size prima di scegliere. Il nome scelto entra nel catalogo del server come variante (o ne aggiorna i numeri, se c'è già); quale variante usa ogni conto non cambia.`}
            </p>
            <div className="space-y-2">
              {found.candidates.map((c) => {
                const attivo = scelto === c.brokerSymbol;
                return (
                  <button
                    key={c.brokerSymbol}
                    type="button"
                    onClick={() => setScelto(c.brokerSymbol)}
                    className={`w-full rounded border px-3 py-2 text-left text-sm transition ${
                      attivo ? "border-accent bg-accent/10" : "border-line hover:border-accent/50"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono font-medium text-ink">{c.brokerSymbol}</span>
                      <span className="text-xs text-faint">{c.profitCurrency}</span>
                    </div>
                    <dl className="mt-1 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-muted tabular-nums">
                      <div>
                        <dt className="inline text-faint">contract size </dt>
                        <dd className="inline text-ink">{num(c.contractSize)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">valore punto </dt>
                        <dd className="inline text-ink">{num(c.valuePerPoint)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">lot step </dt>
                        <dd className="inline text-ink">{num(c.lotStep)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">lotti </dt>
                        <dd className="inline text-ink">
                          {num(c.minLot)}–{num(c.maxLot)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">digits </dt>
                        <dd className="inline text-ink">{c.digits}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">stops level </dt>
                        <dd className="inline text-ink">{c.stopsLevel}</dd>
                      </div>
                    </dl>
                    {c.blocker && <p className="mt-1 text-xs text-warn">Si salva, ma resta non verificato: {c.blocker}.</p>}
                    {distinctSymbolWarnings(c.warnings).map((w) => (
                      <p key={w.code} className="mt-1 text-xs text-warn">
                        {w.message}
                      </p>
                    ))}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={onManuale} className="text-xs text-faint underline">
              Nessuno di questi: scrivo a mano (non verificato)
            </button>
          </>
        )}
      </div>
    </SidePanel>
  );
}

function SymbolManualForm({
  server,
  symbol,
  canonical: presetCanonical,
  peersOf,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  peersOf: (serverId: string, canonical: string) => string[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [canonical, setCanonical] = useState(symbol?.canonical ?? presetCanonical ?? "");
  const [brokerSymbol, setBrokerSymbol] = useState(symbol?.brokerSymbol ?? presetCanonical ?? "");
  const [digits, setDigits] = useState(symbol ? String(symbol.digits) : "");
  const [point, setPoint] = useState(symbol ? trim(symbol.point) : "");
  // Nessun valore predefinito: 1.0 vale su XAUUSD e non altrove.
  const [valuePerPoint, setValuePerPoint] = useState(symbol ? trim(symbol.valuePerPoint) : "");
  const [lotStep, setLotStep] = useState(symbol ? trim(symbol.lotStep) : "");
  const [minLot, setMinLot] = useState(symbol ? trim(symbol.minLot) : "");
  const [stopsLevel, setStopsLevel] = useState(symbol ? String(symbol.stopsLevel) : "0");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Gli avvisi si vedono mentre si scrive, PRIMA di salvare, e non bloccano: un
   * numero strano può essere vero (i broker esotici esistono), ma va guardato
   * due volte. Da qui esce il valore punto che decide i punti di SL e TP.
   */
  const avvisi: SymbolSpecWarning[] = distinctSymbolWarnings(
    symbolSpecWarnings(
      {
        canonical,
        digits,
        point,
        valuePerPoint,
        lotStep,
        minLot,
        maxLot: symbol?.maxLot,
      },
      { serverKind: server.kind, peers: peersOf(server.id, canonical.trim().toUpperCase()) },
    ),
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: SymbolInput = {
      canonical: canonical.trim().toUpperCase(),
      brokerSymbol: brokerSymbol.trim(),
      digits: intInput(digits) ?? NaN,
      point: decimalInput(point) ?? "",
      valuePerPoint: decimalInput(valuePerPoint) ?? "",
      lotStep: decimalInput(lotStep) ?? "",
      minLot: decimalInput(minLot) ?? "",
      stopsLevel: intInput(stopsLevel) ?? NaN,
    };
    const invalid = [
      Number.isNaN(body.digits) && "digits",
      !body.point && "point",
      !body.valuePerPoint && "valore punto",
      !body.lotStep && "lot step",
      !body.minLot && "lotto minimo",
      Number.isNaN(body.stopsLevel) && "stops level",
    ].filter(Boolean);
    if (invalid.length) return setError(`Controlla: ${invalid.join(", ")}. Usa numeri, per esempio 0.01.`);
    if (Number(body.valuePerPoint) <= 0) return setError("Il valore punto deve essere maggiore di zero");

    setBusy(true);
    try {
      if (symbol) await api.servers.updateSymbol(server.id, symbol.id, body);
      else await api.servers.addSymbol(server.id, body);
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const numField = (label: string, value: string, set: (v: string) => void, hint?: string, placeholder?: string) => (
    <Field label={label} hint={hint}>
      {(id) => (
        <input id={id} required inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => set(e.target.value)} className={inputClass} />
      )}
    </Field>
  );

  return (
    <SidePanel
      open
      onClose={onClose}
      title={symbol ? `Modifica ${symbol.canonical}` : "Simbolo a mano"}
      subtitle={`${server.displayName ?? server.name} · valori scritti a mano: restano NON verificati`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="symbol-form" disabled={busy}>
            {busy ? "Salvataggio…" : symbol ? "Salva" : "Aggiungi simbolo"}
          </Button>
        </>
      }
    >
      <form id="symbol-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Simbolo canonico" hint="Uguale su tutti i server, per esempio XAUUSD.">
            {(id) => <input id={id} required value={canonical} onChange={(e) => setCanonical(e.target.value.toUpperCase())} className={inputClass} />}
          </Field>
          <Field label="Simbolo sul server" hint="Com'è scritto qui, per esempio XAUUSD-E.">
            {(id) => <input id={id} required value={brokerSymbol} onChange={(e) => setBrokerSymbol(e.target.value)} className={inputClass} />}
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {numField("Digits", digits, setDigits, undefined, "2")}
          {numField("Point", point, setPoint, "Valore di un punto in prezzo.", "0.01")}
        </div>
        {numField(
          "Valore punto",
          valuePerPoint,
          setValuePerPoint,
          "Valore di 1 punto su 1 lotto, nella valuta del conto. Leggilo dal terminale: vale 1 su XAUUSD, non su altri simboli, e un valore sbagliato sbaglia tutti i lotti.",
        )}
        <div className="grid grid-cols-3 gap-3">
          {numField("Lot step", lotStep, setLotStep, undefined, "0.01")}
          {numField("Lotto minimo", minLot, setMinLot, undefined, "0.01")}
          {numField("Stops level", stopsLevel, setStopsLevel, undefined, "0")}
        </div>
        <p className="text-xs text-faint">
          Il lot step decide l'arrotondamento per difetto del lotto su questo server: le due gambe usano ciascuna il proprio.
        </p>
        {avvisi.length > 0 && (
          <div role="status" className="space-y-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            {avvisi.map((a) => (
              <p key={a.code}>{a.message}</p>
            ))}
          </div>
        )}
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}


// ---------------------------------------------------------------------------

/**
 * Il valore punto dello stesso simbolo sugli ALTRI server. È il confronto che
 * fa notare un numero fuori scala senza dover sapere niente del simbolo: se un
 * server dice cento volte meno di tutti gli altri, uno dei due sbaglia.
 */
export function usePeersOf() {
  const servers = useServers();
  return (serverId: string, canonical: string) =>
    (servers.data ?? [])
      .filter((s) => s.id !== serverId)
      .flatMap((s) => s.symbols.filter((m) => m.canonical === canonical).map((m) => m.valuePerPoint));
}

/** I canonici che su questo server non hanno una riga. `extra`: gli indici, solo con la capacita' attiva (`useIndexSymbols`). */
export const canonicalsToMap = (server: BrokerServer, extra: readonly string[] = []): string[] => {
  // Una variante qualunque nel catalogo basta: il canonico c'e'.
  const mappati = new Set(server.symbols.map((s) => s.canonical));
  return [...CANONICAL_SYMBOLS, ...extra].filter((c) => !mappati.has(c));
};

/** NAS100, US30, WTI se il tenant ha gli indici attivi (`/auth/me`); altrimenti nessuno. Decide il backend. */
export function useIndexSymbols(): readonly string[] {
  return useCurrentUser().capabilities?.indices ? INDEX_SYMBOLS : [];
}

/**
 * Lo stato dei simboli in una riga, senza l'elenco.
 *
 * «Si mappano alla prima connessione» è vero solo finché nessun conto si è
 * collegato: col fuso già letto una connessione c'è stata, la mappatura
 * automatica è passata e non ha trovato niente. Dirlo ancora sarebbe promettere
 * una cosa che non succede.
 */
export function symbolStatus(server: BrokerServer): string {
  if (server.symbols.length === 0) {
    return server.tzOffsetMin === null
      ? "nessun simbolo: si mappano alla prima connessione"
      : "nessun simbolo: la connessione non ne ha mappati, si scelgono a mano";
  }
  const n = new Set(server.symbols.map((s) => s.canonical)).size;
  const varianti = server.symbols.length;
  const mancano = canonicalsToMap(server).length;
  const daScegliere = server.accountsWithoutVariant?.toChoose ?? 0;
  return (
    `${n} ${n === 1 ? "simbolo mappato" : "simboli mappati"}` +
    (varianti > n ? ` in ${varianti} varianti` : "") +
    (mancano > 0 ? ` · ${mancano} da mappare` : "") +
    (daScegliere > 0 ? ` · ${daScegliere} ${daScegliere === 1 ? "conto" : "conti"} con la variante da scegliere` : "")
  );
}

/** «7 conti», «1 conto». */
const contiDi = (n: number) => `${n} ${n === 1 ? "conto" : "conti"}`;

/** L'elenco dei simboli di un server: si vede solo quando lo si apre. */
export function SymbolsTable({
  server,
  canWrite,
  peersOf,
  hasSiblings,
  onAdd,
  onEdit,
  onPropose,
}: {
  server: BrokerServer;
  canWrite: boolean;
  peersOf: (serverId: string, canonical: string) => string[];
  hasSiblings: boolean;
  onAdd: (canonical?: string) => void;
  onEdit: (s: SymbolMapping) => void;
  onPropose: () => void;
}) {
  const isProp = server.kind === "PROP";
  /*
   * I canonici senza riga si RICAVANO: la lista dei canonici è nota
   * (`@match/shared`, la stessa da cui la mappatura automatica cerca i nomi) e
   * le righe scritte sono qui. Nessuna colonna registra il «non trovato», di
   * proposito: sarebbe una terza verità da tenere allineata con le altre due.
   *
   * Sono quelli che la connessione non è riuscita a mappare da sola: o il
   * server non ha un nome che somigli, o ne ha più d'uno e la scelta è di una
   * persona (DECISIONE 27). Il nome apre il pannello già sul canonico giusto;
   * il simbolo del server resta da scegliere, senza preselezione.
   */
  const daMappare = canonicalsToMap(server, useIndexSymbols());
  /** La variante aperta: i suoi dati, Modifica, Elimina. */
  const [aperta, setAperta] = useState<string | null>(null);
  // Una riga per canonico, le sue varianti in ordine di nome.
  const canonici = [...new Set(server.symbols.map((m) => m.canonical))]
    .sort()
    .map((c) => [c, server.symbols.filter((m) => m.canonical === c).sort((a, b) => a.brokerSymbol.localeCompare(b.brokerSymbol))] as const);
  const avvisiDi = (m: SymbolMapping) => distinctSymbolWarnings(symbolSpecWarnings(m, { serverKind: server.kind, peers: peersOf(server.id, m.canonical) }));

  return (
    <div className="border-t border-line">
      {server.tzOffsetMin === null && (
        <p className="border-b border-line bg-warn/10 px-4 py-2 text-sm text-warn">
          Il fuso di questo server non è ancora stato letto dal gateway. Finché manca, gli orari che arrivano da MT5 su questo
          server non si convertono e la mezzanotte non si calcola: niente P&amp;L del giorno e niente DD giornaliero. Si legge da
          sé alla prima connessione di un conto.
        </p>
      )}

      {/* Lo stato delle varianti dei conti del server: cosa resta da fare, e dove. */}
      {(server.legacyAccountRows ?? 0) > 0 && (
        <p className="border-b border-line bg-warn/10 px-4 py-2 text-sm text-warn">
          {server.legacyAccountRows === 1 ? "1 riga di un conto" : `${server.legacyAccountRows} righe di conti`} del 09/10 non ancora nel catalogo: nessuno le usa
          finché non si lancia <span className="machine-id">migrate:symbol-variants</span>.
        </p>
      )}
      {(server.accountsWithoutVariant?.toChoose ?? 0) > 0 && (
        <p className="border-b border-line bg-warn/10 px-4 py-2 text-sm text-warn">
          {contiDi(server.accountsWithoutVariant!.toChoose)} di questo server {server.accountsWithoutVariant!.toChoose === 1 ? "vede" : "vedono"} più varianti dello stesso
          simbolo: finché non si sceglie la loro nella scheda del conto («Quale tipo usa questo conto?») non aprono operazioni.
        </p>
      )}
      {(server.accountsWithoutVariant?.unread ?? 0) > 0 && (
        <p className="border-b border-line px-4 py-2 text-xs text-muted">
          {contiDi(server.accountsWithoutVariant!.unread)} senza variante rilevata: si rileva al prossimo «Connetti». Con più varianti sullo stesso simbolo, fino ad allora non aprono su quel simbolo.
        </p>
      )}

      {server.symbols.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted">
          Nessun simbolo mappato. Aggiungi quello su cui opererai, con le specifiche lette dal terminale: senza, le fasi su questo server non calcolano i lotti.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="w-32 px-4 py-2 font-medium">Canonico</th>
                <th className="py-2 pr-4 font-medium" title="Una riga del catalogo per nome sul server. Ogni conto usa quella della sua variante; tra parentesi i conti che la usano.">
                  Varianti
                </th>
              </tr>
            </thead>
            <tbody>
              {canonici.map(([canonical, righe]) => {
                const scelta = righe.find((m) => m.id === aperta);
                return (
                  <Fragment key={canonical}>
                    <tr className={`border-b border-line/60 ${scelta ? "" : "last:border-0"}`}>
                      <td className="px-4 py-2 align-top font-medium text-ink">{canonical}</td>
                      <td className="py-2 pr-4">
                        <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-1">
                          {righe.map((m, i) => {
                            const avvisi = avvisiDi(m);
                            return (
                              <Fragment key={m.id}>
                                {i > 0 && <span className="text-faint">·</span>}
                                <button
                                  type="button"
                                  onClick={() => setAperta(aperta === m.id ? null : m.id)}
                                  aria-expanded={aperta === m.id}
                                  title={avvisi.length ? avvisi.map((a) => a.message).join(" ") : "Mostra i dati di questa variante"}
                                  className={`rounded px-1.5 py-0.5 text-left hover:bg-raised ${aperta === m.id ? "bg-raised" : ""} ${avvisi.length ? "text-warn" : "text-ink"}`}
                                >
                                  <span className="font-mono">{m.brokerSymbol}</span>
                                  <span className="ml-1 text-xs text-muted tabular-nums">({contiDi(m.accounts ?? 0)})</span>
                                  {!m.syncedAt && <span className="ml-1 text-xs text-warn">a mano</span>}
                                </button>
                              </Fragment>
                            );
                          })}
                        </span>
                      </td>
                    </tr>
                    {scelta && <VariantDetail server={server} m={scelta} avvisi={avvisiDi(scelta)} canWrite={canWrite} onEdit={() => onEdit(scelta)} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(canWrite || daMappare.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-4 py-2 text-xs text-muted">
          {canWrite && (
            <span className="inline-flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => onAdd()}>
                Aggiungi simbolo
              </Button>
              {hasSiblings && daMappare.length > 0 && (
                <Button size="sm" onClick={onPropose}>
                  Proponi dal server fratello
                </Button>
              )}
            </span>
          )}
          {daMappare.length > 0 && (
            <p>
              <span className="text-faint">Da mappare a mano:</span>{" "}
              {daMappare.map((c, i) => (
                <Fragment key={c}>
                  {i > 0 && ", "}
                  {canWrite ? (
                    <button
                      type="button"
                      // Il colore della gamba, come ovunque: prop indaco, broker ambra.
                      className={`font-medium text-ink underline decoration-dotted underline-offset-4 ${isProp ? "hover:text-prop" : "hover:text-broker"}`}
                      onClick={() => onAdd(c)}
                    >
                      {c}
                    </button>
                  ) : (
                    <span className="font-medium text-ink">{c}</span>
                  )}
                </Fragment>
              ))}
              <span className="ml-1 text-faint">
                {server.tzOffsetMin === null
                  ? "— nessun conto di questo server si è ancora collegato: la mappatura automatica parte alla prima connessione."
                  : "— la connessione non ha trovato un nome corrispondente, o ne ha trovati più d'uno: qui sceglie una persona."}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * I DATI DI UNA VARIANTE, sotto la riga del suo canonico: i numeri (quelli fuori
 * scala sottolineati), da dove vengono, e Modifica / Elimina. L'eliminazione
 * di una variante che un conto usa la rifiuta il backend, col nome dei conti.
 */
function VariantDetail({ server, m, avvisi, canWrite, onEdit }: { server: BrokerServer; m: SymbolMapping; avvisi: SymbolSpecWarning[]; canWrite: boolean; onEdit: () => void }) {
  const cell = (field: SymbolSpecField) => {
    const w = avvisi.filter((a) => a.field === field);
    return w.length
      ? { className: "font-medium text-warn underline decoration-warn decoration-dotted underline-offset-4 tabular-nums", title: w.map((a) => a.message).join(" ") }
      : { className: "text-ink tabular-nums" };
  };
  const voce = (etichetta: string, valore: React.ReactNode, props: { className: string; title?: string } = { className: "text-ink tabular-nums" }) => (
    <div>
      <dt className="text-xs text-faint">{etichetta}</dt>
      <dd {...props}>{valore}</dd>
    </div>
  );
  return (
    <tr className="border-b border-line/60 bg-raised/40 last:border-0">
      <td colSpan={2} className="px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4 lg:grid-cols-8">
            {voce("Simbolo sul server", <span className="font-mono">{m.brokerSymbol}</span>, { className: "text-ink" })}
            {voce("Variante", m.variant === undefined ? "—" : m.variant === "" ? "senza suffisso" : <span className="font-mono">{m.variant}</span>, { className: "text-ink" })}
            {voce("Digits", m.digits)}
            {voce("Point", trim(m.point), cell("point"))}
            {voce("Valore punto", trim(m.valuePerPoint), cell("valuePerPoint"))}
            {voce("Lot step", trim(m.lotStep), cell("lotStep"))}
            {voce("Lotto minimo", trim(m.minLot), cell("minLot"))}
            {voce("Stops level", m.stopsLevel)}
          </dl>
          {canWrite && (
            <span className="inline-flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" onClick={onEdit}>
                Modifica
              </Button>
              {/* `XAUUSD.m` scelto al posto di `XAUUSD` si cancella: le operazioni gia'
                  registrate tengono il nome e restano leggibili. Una variante che un
                  conto usa no: il backend lo rifiuta dicendo quali. */}
              <DeleteForever kind="simbolo" id={m.id} serverId={server.id} label={`${m.canonical} → ${m.brokerSymbol}`} size="sm" />
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted">
          {/* Da dove vengono i numeri: letti dal broker o scritti a mano. */}
          <span className={m.syncedAt ? "" : "text-warn"}>{m.syncedAt ? `Letti dal broker il ${new Date(m.syncedAt).toLocaleDateString("it-IT")}` : "Scritti a mano: mai confrontati col broker"}</span>
          {" · "}
          {m.accounts ? `usata da ${contiDi(m.accounts)} con la variante assegnata` : "nessun conto con la variante assegnata la usa"}
        </p>
        {avvisi.length > 0 && (
          <p className="mt-1 text-xs text-warn">
            {avvisi.map((a) => (
              <span key={a.code} className="block">
                {a.message}
              </span>
            ))}
          </p>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

/** Una riga della proposta: il nome del fratello, e cosa risponde questo server. */
type Riga =
  | { canonical: string; nome: string; valoreFratello: string; stato: "letto"; candidato: SymbolCandidate }
  | { canonical: string; nome: string; valoreFratello: string; stato: "assente"; simili: number }
  | { canonical: string; nome: string; valoreFratello: string; stato: "errore"; messaggio: string };

/**
 * LA MAPPATURA DI UN SERVER FRATELLO, PROPOSTA E MAI APPLICATA DA SOLA.
 *
 * Due server della stessa firm o dello stesso broker hanno spesso gli stessi
 * nomi, ma non è una regola: i nomi dei simboli e il valore punto cambiano da un
 * server all'altro. Quindi:
 *
 *  - dal fratello si prende **solo il nome**; i numeri li rilegge dal gateway il
 *    backend su QUESTO server (`syncSymbol`, la stessa strada del «Rileva»);
 *  - la proposta si legge PRIMA (`candidates`), e ogni riga mostra i numeri di
 *    qui accanto al valore punto del fratello: se non tornano, si vede;
 *  - **nessuna riga è preselezionata**: si spunta quella che si accetta, e
 *    niente si scrive finché non si conferma. Se il nome non c'è su questo
 *    server la riga non si può spuntare: resta da mappare a mano;
 *  - senza un conto collegato su questo server non c'è niente da leggere, e la
 *    proposta non parte (nessun ripiego copiando i numeri del fratello).
 */
export function SiblingProposalPanel({
  server,
  siblings,
  onClose,
}: {
  server: BrokerServer;
  siblings: BrokerServer[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [siblingId, setSiblingId] = useState(siblings.length === 1 ? (siblings[0]?.id ?? "") : "");
  const sibling = siblings.find((s) => s.id === siblingId);
  const [rows, setRows] = useState<Riga[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<"leggi" | "applica" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [esiti, setEsiti] = useState<Record<string, { ok: boolean; testo: string }>>({});

  const daMappare = canonicalsToMap(server);
  // Dal fratello un nome per canonico (la prima variante): le altre varianti le porta qui la connessione dei conti.
  const proposte = sibling
    ? sibling.symbols.filter((m, i, tutte) => daMappare.includes(m.canonical) && tutte.findIndex((x) => x.canonical === m.canonical) === i)
    : [];
  const lettore = useReadingAccount(server.id);
  const num = (x: number | string) => String(Number(x));

  const leggi = async () => {
    if (!sibling) return;
    if (needsChoice(lettore)) return setError("Scegli da quale conto leggere: su questo server ne sono collegati più d'uno.");
    setError(null);
    setEsiti({});
    setPicked(new Set());
    setRows(null);
    setBusy("leggi");
    const out: Riga[] = [];
    try {
      // Una alla volta: ogni lettura apre una richiesta al gateway.
      for (const m of proposte) {
        const base = { canonical: m.canonical, nome: m.brokerSymbol, valoreFratello: trim(m.valuePerPoint) };
        try {
          const r: SymbolCandidates = await api.servers.candidates(server.id, m.canonical, lettore.scelto);
          const candidato = r.candidates.find((c) => c.brokerSymbol === m.brokerSymbol);
          out.push(candidato ? { ...base, stato: "letto", candidato } : { ...base, stato: "assente", simili: r.candidates.length });
        } catch (err) {
          // Senza conto collegato nessuna riga potrà essere letta: si ferma qui.
          if (out.length === 0 && /conto collegato/i.test(errorMessage(err))) throw err;
          out.push({ ...base, stato: "errore", messaggio: errorMessage(err) });
        }
      }
      setRows(out);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const spuntabili = (rows ?? []).filter((r) => r.stato === "letto" && !r.candidato.blocker);
  const toggle = (c: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      return n;
    });

  const applica = async () => {
    setError(null);
    setBusy("applica");
    const nuovi: Record<string, { ok: boolean; testo: string }> = {};
    for (const r of rows ?? []) {
      if (!picked.has(r.canonical) || r.stato !== "letto") continue;
      try {
        await api.servers.syncSymbol(server.id, { canonical: r.canonical, brokerSymbol: r.nome, accountId: lettore.scelto });
        nuovi[r.canonical] = { ok: true, testo: "mappato" };
      } catch (err) {
        nuovi[r.canonical] = { ok: false, testo: errorMessage(err) };
      }
    }
    setEsiti(nuovi);
    // Chi è stato scritto non si ripropone: resta spuntato solo chi ha fallito.
    setPicked(new Set(Object.entries(nuovi).filter(([, e]) => !e.ok).map(([c]) => c)));
    await refresh();
    setBusy(null);
    if (Object.values(nuovi).every((e) => e.ok)) onClose();
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Proponi dal server fratello"
      subtitle={`${server.displayName ?? server.name} · i nomi dal fratello, i numeri da questo server, la conferma è tua`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
          <Button variant="primary" onClick={applica} disabled={picked.size === 0 || busy !== null}>
            {busy === "applica" ? "Salvataggio…" : picked.size ? `Mappa ${picked.size} ${picked.size === 1 ? "simbolo" : "simboli"}` : "Spunta cosa accettare"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-faint">
          È una proposta. I nomi dei simboli e il valore punto cambiano da un server all&apos;altro, quindi dal fratello si
          prende solo il nome: i numeri li legge il gateway su questo server, e niente si scrive finché non spunti e confermi.
        </p>

        <ReadingAccountPicker r={lettore} />

        {siblings.length > 1 && (
          <Field label="Server fratello">
            {(id) => (
              <select
                id={id}
                value={siblingId}
                onChange={(e) => {
                  setSiblingId(e.target.value);
                  setRows(null);
                  setPicked(new Set());
                  setEsiti({});
                }}
                className={inputClass}
              >
                <option value="">Scegli il server</option>
                {siblings.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName ?? s.name} · {s.symbols.length} {s.symbols.length === 1 ? "simbolo" : "simboli"}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {siblings.length === 1 && sibling && <p className="text-sm text-muted">Dal server {sibling.displayName ?? sibling.name}.</p>}

        {sibling && proposte.length === 0 && <p className="text-sm text-muted">Il fratello non ha nessuno dei simboli che qui mancano.</p>}
        {sibling && proposte.length > 0 && (
          <Button onClick={leggi} disabled={busy !== null}>
            {busy === "leggi" ? "Lettura…" : rows ? "Rileggi da questo server" : `Leggi ${proposte.length} ${proposte.length === 1 ? "simbolo" : "simboli"} su questo server`}
          </Button>
        )}

        <ErrorNote>{error}</ErrorNote>

        {rows && (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-faint">Nessuna riga è preselezionata.</p>
              {spuntabili.length > 0 && (
                <button type="button" className="text-xs text-muted underline" onClick={() => setPicked(new Set(spuntabili.map((r) => r.canonical)))}>
                  Spunta tutte quelle trovate
                </button>
              )}
            </div>
            <div className="space-y-2">
              {rows.map((r) => {
                const esito = esiti[r.canonical];
                const attivo = picked.has(r.canonical);
                const libero = r.stato === "letto" && !r.candidato.blocker;
                return (
                  <label
                    key={r.canonical}
                    className={`block rounded border px-3 py-2 text-sm ${attivo ? "border-accent bg-accent/10" : "border-line"} ${libero ? "cursor-pointer" : "opacity-80"}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <input type="checkbox" checked={attivo} disabled={!libero || busy !== null} onChange={() => toggle(r.canonical)} />
                      <span className="font-medium text-ink">{r.canonical}</span>
                      <span className="text-muted">→</span>
                      <span className="font-mono text-ink">{r.nome}</span>
                    </div>
                    {r.stato === "letto" && (
                      <dl className="mt-1 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-muted tabular-nums">
                        <div>
                          <dt className="inline text-faint">valore punto qui </dt>
                          <dd className="inline text-ink">{num(r.candidato.valuePerPoint)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">sul fratello </dt>
                          <dd className="inline text-ink">{r.valoreFratello}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">contract size </dt>
                          <dd className="inline text-ink">{num(r.candidato.contractSize)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">lot step </dt>
                          <dd className="inline text-ink">{num(r.candidato.lotStep)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">digits </dt>
                          <dd className="inline text-ink">{r.candidato.digits}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">stops level </dt>
                          <dd className="inline text-ink">{r.candidato.stopsLevel}</dd>
                        </div>
                      </dl>
                    )}
                    {r.stato === "letto" && r.candidato.blocker && <p className="mt-1 text-xs text-warn">Non si può mappare da qui: {r.candidato.blocker}.</p>}
                    {r.stato === "letto" &&
                      distinctSymbolWarnings(r.candidato.warnings).map((w) => (
                        <p key={w.code} className="mt-1 text-xs text-warn">
                          {w.message}
                        </p>
                      ))}
                    {r.stato === "assente" && (
                      <p className="mt-1 text-xs text-warn">
                        Su questo server non c&apos;è un simbolo con questo nome{r.simili > 0 ? ` (ce ne sono ${r.simili} simili)` : ""}: si mappa a mano.
                      </p>
                    )}
                    {r.stato === "errore" && <p className="mt-1 text-xs text-warn">{r.messaggio}</p>}
                    {esito && <p className={`mt-1 text-xs ${esito.ok ? "text-muted" : "text-loss"}`}>{esito.testo}</p>}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>
    </SidePanel>
  );
}
